// utils/sulu/getGeodata.ts
import { ILocation } from '../calculateDistance';
import getCsrfToken from './getCsrfToken';

export class GeodataAPIError extends Error {
  constructor(
    message: string,
    public type:
      | 'HTTP'
      | 'OVER_QUERY_LIMIT'
      | 'API_STATUS'
      | 'INVALID_RESPONSE'
      | 'NETWORK',
  ) {
    super(message);
    this.name = 'GeodataAPIError';
  }
}

export default async (address: string): Promise<ILocation> => {
  try {
    const token = await getCsrfToken('/geolocation/getCSRF');
    const response = await fetch(
      `/geolocation/by-address/${address}?_token=${token}`,
    );

    // Check for HTTP errors first
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        // If JSON parsing fails, it might not be a JSON response
        console.warn('Could not parse error response as JSON:', e);
      }
      console.error('Geodata API HTTP Error:', response.status, errorData);

      // Distinguish between different HTTP-related issues
      if (errorData && (errorData as any)[0]?.status === 'OVER_QUERY_LIMIT') {
        throw new GeodataAPIError(
          'Die Standortsuche ist derzeit nicht verfügbar, da das tägliche Limit erreicht wurde. Bitte versuchen Sie es später erneut.',
          'OVER_QUERY_LIMIT',
        );
      } else {
        throw new GeodataAPIError(
          'Ein Fehler bei der Standortsuche ist aufgetreten. Bitte versuchen Sie es später erneut.',
          'HTTP',
        );
      }
    }

    const jsonResponse = await response.json();

    // Check for Google's specific status within the JSON (if your backend forwards it)
    if (jsonResponse[0] && jsonResponse[0].status) {
      if (jsonResponse[0].status === 'OVER_QUERY_LIMIT') {
        throw new GeodataAPIError(
          'Für die angegebene Adresse konnten keine genauen Koordinaten gefunden werden. Die tägliche Abfragegrenze wurde erreicht. Es wird ein Standardstandort genutzt.',
          'OVER_QUERY_LIMIT',
        );
      }
      if (
        jsonResponse[0].status !== 'OK' &&
        jsonResponse[0].status !== 'ZERO_RESULTS' // ZERO_RESULTS means no exact match, but not necessarily an API error
      ) {
        console.error(
          'Google Maps API Status-Fehler:',
          jsonResponse[0].status,
          jsonResponse[0].error_message,
        );
        throw new GeodataAPIError(
          'Ein Fehler bei der Google Maps API ist aufgetreten. Bitte versuchen Sie es später erneut.',
          'API_STATUS',
        );
      }
    }

    if (
      jsonResponse[0] &&
      jsonResponse[0].results &&
      jsonResponse[0].results[0] &&
      jsonResponse[0].results[0].geometry &&
      jsonResponse[0].results[0].geometry.location
    ) {
      return jsonResponse[0].results[0].geometry.location;
    }

    console.warn(
      'No exact coordinates found or unexpected response structure:',
      jsonResponse,
    );
    throw new GeodataAPIError(
      "Für die angegebene Adresse konnten keine genauen Koordinaten gefunden werden. Es wird ein Standardstandort genutzt. Die Suche nach Kategorie, Name oder anderen Kriterien funktioniert weiterhin uneingeschränkt. Beachten Sie: Wenn das Feld 'Ort oder PLZ' leer ist, wird versucht, den Standort Ihres Geräts zu nutzen (sofern Sie dies zulassen). Wenn 'Moorweg 2, 24582 Bordesholm' eingegeben wird, wird dieser spezifische Standort für die Suche verwendet.",
      'INVALID_RESPONSE',
    );
  } catch (e: any) {
    console.error('Final catch in getGeodata:', e);
    // If it's a GeodataAPIError, re-throw it directly
    if (e instanceof GeodataAPIError) {
      throw e;
    }
    // For any other unexpected error (e.g., network issues before fetch resolves)
    throw new GeodataAPIError(
      'Es gab ein Problem bei der Verbindung zur Standortsuche. Bitte überprüfen Sie Ihre Internetverbindung.',
      'NETWORK',
    );
  }
};
